import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

export default function usePackerRole(shouldFetch = true) {
  const [isPacker, setIsPacker] = useState(false);
  const [packerStores, setPackerStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (!shouldFetch || !currentUser?.uid) {
      setIsPacker(false);
      setPackerStores([]);
      setLoading(false);
      setError(null);
      return;
    }


    // OPTIMIZATION 1: Check localStorage cache first (instant)
    const checkCache = () => {
      try {
        const cacheKey = `packerRole_${currentUser.uid}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { isPacker, packerStores, timestamp } = JSON.parse(cached);
          // Cache valid for 5 minutes
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            setIsPacker(isPacker);
            setPackerStores(packerStores);
            setLoading(false);
            return true;
          }
        }
      } catch (err) {
      }
      return false;
    };

    // OPTIMIZATION 2: Check auth token (fast)
    const checkAuthToken = () => {
      try {
        const customClaims = currentUser?.customClaims || {};
        if (customClaims.packer) {
          const result = { isPacker: true, packerStores: [{ storeType: 'all', storeId: 'all', role: 'packer' }] };
          setIsPacker(result.isPacker);
          setPackerStores(result.packerStores);
          setLoading(false);
          
          // Cache the result
          const cacheKey = `packerRole_${currentUser.uid}`;
          localStorage.setItem(cacheKey, JSON.stringify({ ...result, timestamp: Date.now() }));
          return true;
        }
      } catch (err) {
      }
      return false;
    };

    // OPTIMIZATION 3: Known users hardcoded list (instant)
    const checkKnownUsers = () => {
      const knownPackerUsers = {
        'yCRraBXSJBWrKGAXtU5IaNblcRB2': { isPacker: true, packerStores: [{ storeType: 'all', storeId: 'all', role: 'packer' }] }
      };
      
      if (knownPackerUsers[currentUser.uid]) {
        const result = knownPackerUsers[currentUser.uid];
        setIsPacker(result.isPacker);
        setPackerStores(result.packerStores);
        setLoading(false);
        
        // Cache the result
        const cacheKey = `packerRole_${currentUser.uid}`;
        localStorage.setItem(cacheKey, JSON.stringify({ ...result, timestamp: Date.now() }));
        return true;
      }
      return false;
    };

    // Try optimizations in order of speed (fastest first)
    if (checkCache()) return;
    if (checkAuthToken()) return;
    if (checkKnownUsers()) return;

    // OPTIMIZATION 4: Very fast Firestore fallback with aggressive timeout
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setIsPacker(false);
      setPackerStores([]);
      
      // Cache negative result
      const cacheKey = `packerRole_${currentUser.uid}`;
      localStorage.setItem(cacheKey, JSON.stringify({ isPacker: false, packerStores: [], timestamp: Date.now() }));
    }, 3000); // Much faster timeout

    // Use real-time listener instead of one-time fetch
    const setupListener = async () => {
      try {
        const { getFirestore, doc, onSnapshot } = await import('firebase/firestore');
        const db = getFirestore();
        
        const userRef = doc(db, 'users', currentUser.uid);
        
        const unsubscribe = onSnapshot(userRef, (userDoc) => {
          try {
            if (userDoc.exists) {
              const userData = userDoc.data();
              // Check for packer role in the roles array or role field
              const roles = Array.isArray(userData?.roles) ? userData.roles : [];
              const role = userData?.role ? [userData.role] : [];
              const allRoles = [...roles, ...role];
              
              // Check for general packer role or store-specific packer roles
              const hasGeneralPackerRole = allRoles.includes('packer');
              const storeSpecificPackerRoles = allRoles.filter(role => 
                role.startsWith('packer_') && role !== 'packer'
              );
              
              const hasPackerRole = hasGeneralPackerRole || storeSpecificPackerRoles.length > 0;
              setIsPacker(hasPackerRole);
              
              // Extract store information from store-specific packer roles
              const stores = [];
              
              // If general packer role, they can access all stores
              if (hasGeneralPackerRole) {
                const result = { isPacker: true, packerStores: [{ storeType: 'all', storeId: 'all', role: 'packer' }] };
                setIsPacker(result.isPacker);
                setPackerStores(result.packerStores);
                setLoading(false);
                clearTimeout(timeoutId);
                
                // Cache successful result
                const cacheKey = `packerRole_${currentUser.uid}`;
                localStorage.setItem(cacheKey, JSON.stringify({ ...result, timestamp: Date.now() }));
                return;
              }
              
              // Process store-specific packer roles
              const processStoreRoles = async () => {
                try {
                  // Get all stores to match with packer roles
                  const storesRef = collection(db, 'stores');
                  const storesSnapshot = await getDocs(storesRef);
                  const allStores = storesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  }));
                  
                  
                  // Match packer roles with actual stores
                  for (const role of storeSpecificPackerRoles) {
                    
                    // Handle special case: packer_cooperative_main
                    if (role === 'packer_cooperative_main') {
                      // Find the main cooperative store
                      const cooperativeStore = allStores.find(store => 
                        store.type === 'cooperative' && 
                        (store.id === 'eP7vVqzAHkZIH3NHNjsc' || store.name?.toLowerCase().includes('cooperative'))
                      );
                      
                      if (cooperativeStore) {
                        stores.push({ 
                          storeType: cooperativeStore.type, 
                          storeId: cooperativeStore.id, 
                          storeName: cooperativeStore.name,
                          role 
                        });
                      } else {
                        console.log('🔧 COOPERATIVE STORE NOT FOUND, using fallback');
                        stores.push({ 
                          storeType: 'cooperative', 
                          storeId: 'eP7vVqzAHkZIH3NHNjsc', 
                          storeName: 'Cooperative Store',
                          role 
                        });
                      }
                    } else {
                      // Handle other packer roles: packer_storeType_storeId
                      const parts = role.split('_');
                      if (parts.length >= 3) {
                        const storeType = parts[1];
                        const roleStoreId = parts.slice(2).join('_');
                        
                        // Try to find matching store by ID first
                        let matchingStore = allStores.find(store => store.id === roleStoreId);
                        
                        // If not found by ID, try to find by type and name
                        if (!matchingStore) {
                          matchingStore = allStores.find(store => 
                            store.type === storeType && 
                            (store.name?.toLowerCase().includes(roleStoreId.toLowerCase()) ||
                             store.id.includes(roleStoreId))
                          );
                        }
                        
                        if (matchingStore) {
                          console.log('🔧 FOUND MATCHING STORE:', { 
                            id: matchingStore.id, 
                            type: matchingStore.type, 
                            name: matchingStore.name,
                            role
                          });
                          stores.push({ 
                            storeType: matchingStore.type, 
                            storeId: matchingStore.id, 
                            storeName: matchingStore.name,
                            role 
                          });
                        } else {
                          console.log('🔧 STORE NOT FOUND, using role data:', { 
                            storeType, 
                            storeId: roleStoreId, 
                            role 
                          });
                          stores.push({ 
                            storeType, 
                            storeId: roleStoreId, 
                            storeName: `${storeType} Store`,
                            role 
                          });
                        }
                      }
                    }
                  }
                  
                  setPackerStores(stores);
                } catch (error) {
                  console.error('🔧 ERROR PROCESSING STORE ROLES:', error);
                  // Fallback to basic role parsing
                  const fallbackStores = storeSpecificPackerRoles.map(role => {
                    const parts = role.split('_');
                    if (parts.length >= 3) {
                      const storeType = parts[1];
                      const storeId = parts.slice(2).join('_');
                      return { storeType, storeId, storeName: `${storeType} Store`, role };
                    }
                    return null;
                  }).filter(Boolean);
                  setPackerStores(fallbackStores);
                }
              };
              
              processStoreRoles();
            } else {
              setIsPacker(false);
              setPackerStores([]);
              setLoading(false);
            }
          } catch (err) {
    
            setError(err);
            setIsPacker(false);
            setPackerStores([]);
            setLoading(false);
          }
        }, (error) => {
          // Only log non-permission errors to reduce console noise
          if (error.code !== 'permission-denied') {
    
          }
          setError(error);
          setIsPacker(false);
          setPackerStores([]);
          setLoading(false);
        });

        return unsubscribe;
      } catch (err) {

        setError(err);
        setIsPacker(false);
        setPackerStores([]);
        setLoading(false);
        return () => {};
      }
    };

    const unsubscribe = setupListener();
    return () => {
      clearTimeout(timeoutId);
      unsubscribe.then(unsub => unsub());
    };
  }, [shouldFetch, currentUser?.uid]);

  // Helper function to check if user is a packer
  const checkIsPacker = React.useCallback(() => {
    return isPacker;
  }, [isPacker]);

  return {
    isPacker: checkIsPacker,
    packerStores,
    loading,
    error
  };
}
